import {
  Cartesian3,
  Matrix3,
  Matrix4,
  Quaternion,
  HeadingPitchRoll,
  Transforms,
  Math as CesiumMath,
} from "cesium";

// createOrthonormalBasisFromNormal
const normalScratch = new Cartesian3();
const localUpScratch = new Cartesian3(0, 0, 1);
const referenceVectorScratch = new Cartesian3();
const axis1Scratch = new Cartesian3();
const axis2Scratch = new Cartesian3();
// calculateTranslationInLocalSpace
const translationECEFScratch = new Cartesian3();
const localRotationScratch = new Matrix3();
const localTranslationScratch = new Cartesian3();
// orthonormalizeRotationMatrix
const quaternionScratch = new Quaternion();
// calculateRelativeRotationENU
const enuToWorldRotationScratch = new Matrix3();
const worldToEnuRotationScratch = new Matrix3();
const originalRotationWorldScratch = new Matrix3();
const currentRotationWorldScratch = new Matrix3();
const originalRotationTransposedScratch = new Matrix3();
const relativeRotationWorldScratch = new Matrix3();
const tempRotationScratch = new Matrix3();
const relativeRotationEnuScratch = new Matrix3();
const relativeQuaternionEnuScratch = new Quaternion();

export class TransformUtils {
  /**
   * Creates an orthonormal basis from the given normal vector, ensuring Z-axis points upward
   * @param normalInLocalSpace - The normal vector in local ENU coordinates (will be normalized if not already)
   * @returns An orthonormal basis {x, y, z} forming a right-handed coordinate system with Z pointing up
   */
  static createOrthonormalBasisFromNormal(normalInLocalSpace: Cartesian3): {
    x: Cartesian3;
    y: Cartesian3;
    z: Cartesian3;
  } {
    // Step 1: Create an initial orthonormal basis with the normal as Z
    const normal = Cartesian3.clone(normalInLocalSpace, normalScratch);
    Cartesian3.normalize(normal, normal);

    // Calculate X-axis perpendicular to the normal
    const localUp = Cartesian3.clone(Cartesian3.UNIT_Z, localUpScratch);
    const dot = Math.abs(Cartesian3.dot(normal, localUp));
    const referenceVector = Cartesian3.clone(
      dot > 0.9 ? Cartesian3.UNIT_X : Cartesian3.UNIT_Z,
      referenceVectorScratch,
    );

    // X = reference × normal (normalized)
    const axis1 = Cartesian3.cross(referenceVector, normal, axis1Scratch);
    Cartesian3.normalize(axis1, axis1);

    // Y = normal × X (completes right-handed orthonormal basis)
    const axis2 = Cartesian3.cross(normal, axis1, axis2Scratch);
    Cartesian3.normalize(axis2, axis2);

    // Step 2: Determine which axis has the largest dot product with "up" (0,0,1)
    const dotAxis1 = Math.abs(Cartesian3.dot(axis1, localUp));
    const dotAxis2 = Math.abs(Cartesian3.dot(axis2, localUp));
    const dotNormal = Math.abs(Cartesian3.dot(normal, localUp));

    // Step 3: Apply cyclic permutation to make the axis most aligned with "up" become Z
    let newLocalX: Cartesian3;
    let newLocalY: Cartesian3;
    let newLocalZ: Cartesian3;

    if (dotNormal >= dotAxis1 && dotNormal >= dotAxis2) {
      // Normal is already most aligned with up, keep original assignment
      newLocalX = axis1;
      newLocalY = axis2;
      newLocalZ = normal;
    } else if (dotAxis1 >= dotAxis2) {
      // axis1 is most aligned with up, cyclic permutation: Z=axis1, X=axis2, Y=normal
      newLocalX = axis2;
      newLocalY = normal;
      newLocalZ = axis1;
    } else {
      // axis2 is most aligned with up, cyclic permutation: Z=axis2, X=normal, Y=axis1
      newLocalX = normal;
      newLocalY = axis1;
      newLocalZ = axis2;
    }

    // Step 4: Ensure Z points in the positive up direction (not down)
    if (Cartesian3.dot(newLocalZ, localUp) < 0) {
      // Flip Z to point up, and flip X to maintain right-handed system
      Cartesian3.negate(newLocalZ, newLocalZ);
      Cartesian3.negate(newLocalX, newLocalX);
    }

    return { x: newLocalX, y: newLocalY, z: newLocalZ };
  }

  /**
   * Calculates translation in local space from world positions
   * @param currentPosition - The current position in world coordinates
   * @param originalPosition - The original position in world coordinates
   * @param localTransform - The local transform matrix (ENU frame)
   * @returns The translation in local ENU coordinates (east, north, up) rounded to mm precision
   */
  static calculateTranslationInLocalSpace(
    currentPosition: Cartesian3,
    originalPosition: Cartesian3,
    localTransform: Matrix4,
  ): Cartesian3 {
    // Calculate translation in ECEF coordinates
    const translationECEF = Cartesian3.subtract(
      currentPosition,
      originalPosition,
      translationECEFScratch,
    );

    // Get the rotation matrix from local transform
    const localRotation = Matrix4.getMatrix3(
      localTransform,
      localRotationScratch,
    );
    // Transpose to get world-to-local rotation (inverse for orthonormal matrix)
    Matrix3.transpose(localRotation, localRotation);

    // Transform ECEF translation to local transform space
    const localTranslation = Matrix3.multiplyByVector(
      localRotation,
      translationECEF,
      localTranslationScratch,
    );

    // Round to mm precision
    localTranslation.x = Math.round(localTranslation.x * 1000) / 1000;
    localTranslation.y = Math.round(localTranslation.y * 1000) / 1000;
    localTranslation.z = Math.round(localTranslation.z * 1000) / 1000;

    return localTranslation;
  }

  /**
   * Projects to a proper rotation (det=+1) via quaternion normalization
   * @param rotationMatrix - The rotation matrix to clean up
   * @returns A orthonormalized rotation matrix
   */
  static orthonormalizeRotationMatrix(rotationMatrix: Matrix3): Matrix3 {
    const quaternion = Quaternion.fromRotationMatrix(
      rotationMatrix,
      quaternionScratch,
    );
    Quaternion.normalize(quaternion, quaternion);
    return Matrix3.fromQuaternion(quaternion, new Matrix3());
  }

  /**
   * Calculates the relative rotation in ENU coordinates between two model matrices
   * @param currentModelMatrix - The current model matrix
   * @param originalModelMatrix - The original model matrix
   * @param pivotCenter - The center point for the ENU frame
   * @returns HeadingPitchRoll in degrees, rounded to 3 decimal places
   */
  static calculateRelativeRotationENU(
    currentModelMatrix: Matrix4,
    originalModelMatrix: Matrix4,
    pivotCenter: Cartesian3,
  ): HeadingPitchRoll {
    // Create ENU to World transform at the pivot point
    const enuToWorldTransform = Transforms.eastNorthUpToFixedFrame(pivotCenter);
    const enuToWorldRotation = Matrix4.getMatrix3(
      enuToWorldTransform,
      enuToWorldRotationScratch,
    ); // columns: [East, North, Up] in world
    const worldToEnuRotation = Matrix3.transpose(
      enuToWorldRotation,
      worldToEnuRotationScratch,
    ); // inverse

    // Extract rotation matrices from model matrices (ignore scale/translation)
    const originalRotationWorld = Matrix4.getMatrix3(
      originalModelMatrix,
      originalRotationWorldScratch,
    );
    const currentRotationWorld = Matrix4.getMatrix3(
      currentModelMatrix,
      currentRotationWorldScratch,
    );

    // Re-orthonormalize to mitigate tiny drift
    const originalRotationNormalized =
      TransformUtils.orthonormalizeRotationMatrix(originalRotationWorld);
    const currentRotationNormalized =
      TransformUtils.orthonormalizeRotationMatrix(currentRotationWorld);

    // Calculate relative rotation in world coordinates: current relative to original
    const originalRotationTransposed = Matrix3.transpose(
      originalRotationNormalized,
      originalRotationTransposedScratch,
    );
    const relativeRotationWorld = Matrix3.multiply(
      currentRotationNormalized,
      originalRotationTransposed,
      relativeRotationWorldScratch,
    );

    // Transform relative rotation from world to ENU coordinates
    const tempRotation = Matrix3.multiply(
      worldToEnuRotation,
      relativeRotationWorld,
      tempRotationScratch,
    );
    const relativeRotationEnu = Matrix3.multiply(
      tempRotation,
      enuToWorldRotation,
      relativeRotationEnuScratch,
    );

    // Convert to HeadingPitchRoll in ENU (Z-up, Y-north, X-east)
    const relativeQuaternionEnu = Quaternion.fromRotationMatrix(
      relativeRotationEnu,
      relativeQuaternionEnuScratch,
    );
    Quaternion.normalize(relativeQuaternionEnu, relativeQuaternionEnu);
    const hpr = HeadingPitchRoll.fromQuaternion(
      relativeQuaternionEnu,
      new HeadingPitchRoll(),
    );

    // Normalize angles to [-pi, pi] and round to 3 decimal places (in degrees)
    hpr.heading =
      Math.round(
        CesiumMath.toDegrees(CesiumMath.negativePiToPi(hpr.heading)) * 1000,
      ) / 1000;
    hpr.pitch =
      Math.round(
        CesiumMath.toDegrees(CesiumMath.negativePiToPi(hpr.pitch)) * 1000,
      ) / 1000;
    hpr.roll =
      Math.round(
        CesiumMath.toDegrees(CesiumMath.negativePiToPi(hpr.roll)) * 1000,
      ) / 1000;

    return hpr;
  }
}
